import { useMemo, useState } from "react";
import { Download, FileText, Printer, Scissors, ShieldCheck, Sparkles } from "lucide-react";
import PageShell from "../components/PageShell";
import ShareByEmail from "../components/ShareByEmail";
import ResumeUploadBox from "../components/ResumeUploadBox";
import useSeo from "../hooks/useSeo";
import { api } from "../lib/api";
import { jobCategories } from "../data/content";

const sampleResume = `Alex Morgan
Calgary, Alberta | alex@email.com | 403-555-0101 | LinkedIn: linkedin.com/in/alex

Summary
Administrative and operations professional with experience supporting schedules, documentation, customer communication, and daily office coordination.

Experience
Office Coordinator | North Office Services | 2021-2024
- Coordinated calendars, meetings, and daily office communication for managers and staff.
- Prepared documents, updated spreadsheets, and organized digital records.
- Responded to customer inquiries and helped resolve routine service issues.

Skills
Microsoft Office, scheduling, documentation, customer service, communication, organization

Education
Business Administration Certificate`;

const sampleJob = `Project Coordinator responsibilities:
- Coordinate project schedules, meetings, and follow-up actions.
- Maintain project documentation, status reports, and task trackers.
- Communicate with internal teams, vendors, and clients about timelines and deliverables.
- Support budget tracking, purchase orders, and administrative project tasks.
- Identify risks, escalate delays, and help keep projects on schedule.
- Use Microsoft Office, Excel, and project management tools.`;

function downloadFile(filename, content, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function createWordHtml(resumeText) {
  const escaped = resumeText
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br />");
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Tailored Resume</title></head><body style="font-family: Arial, sans-serif; line-height: 1.5; color: #111827;">${escaped}</body></html>`;
}

export default function ResumeTailor() {
  useSeo("Resume Tailor", "Paste job responsibilities and your resume to tailor the resume for the role with downloadable Word and print/PDF options.");
  const [targetRole, setTargetRole] = useState("");
  const [resumeText, setResumeText] = useState("");
  const [jobResponsibilities, setJobResponsibilities] = useState("");
  const [jobCategory, setJobCategory] = useState(jobCategories[0]);
  const [consentToStore, setConsentToStore] = useState(false);
  const [result, setResult] = useState(null);
  const [editableResume, setEditableResume] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [uploadInfo, setUploadInfo] = useState(null);

  const wordCount = useMemo(() => editableResume.split(/\s+/).filter(Boolean).length, [editableResume]);

  const submit = async (event) => {
    event.preventDefault();
    const trimmedResume = resumeText.trim();
    const trimmedJob = jobResponsibilities.trim();
    if (trimmedJob.length < 80) {
      setError("Please paste the job responsibilities first. The tailored resume and download buttons appear after the tailored version is generated.");
      return;
    }
    if (trimmedResume.length < 120) {
      setError("Please paste or upload more resume text before tailoring. The download buttons appear after the tailored resume is generated.");
      return;
    }
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const response = await api.tailorResume({
        resume_text: trimmedResume,
        job_responsibilities: trimmedJob,
        target_role: targetRole,
        job_category: jobCategory,
        consent_to_store: consentToStore,
      });
      setResult(response.data);
      setEditableResume(response.data.tailored_resume);
    } catch (err) {
      setError(err.response?.data?.detail || "Unable to tailor resume. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const printResume = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.write(createWordHtml(editableResume));
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const downloadWord = () => {
    downloadFile("tailored-resume.doc", createWordHtml(editableResume), "application/msword");
  };

  const downloadText = () => {
    downloadFile("tailored-resume.txt", editableResume, "text/plain;charset=utf-8");
  };

  const downloadOriginalFile = () => {
    if (!uploadInfo?.originalFile) return;
    const originalName = uploadInfo.originalFile.name || "original-resume.docx";
    const safeName = originalName.replace(/(\.[^.]+)?$/, "-with-original-format$1");
    const url = URL.createObjectURL(uploadInfo.originalFile);
    const link = document.createElement("a");
    link.href = url;
    link.download = safeName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <PageShell>
      <section className="bg-white px-5 pb-16 pt-32 md:px-8 lg:pb-24" data-testid="resume-tailor-page-section">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.78fr_1.22fr] lg:items-start">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#0066FF]" data-testid="resume-tailor-eyebrow">Resume builder</p>
            <h1 className="mt-5 font-heading text-4xl font-black leading-tight text-[#0A2540] sm:text-5xl lg:text-6xl" data-testid="resume-tailor-title">Resume Tailor for Job Responsibilities</h1>
            <p className="mt-6 text-base leading-8 text-[#475569] md:text-lg" data-testid="resume-tailor-description">
              Paste the job responsibilities and your resume. The app compares both and gives copy-ready suggested points while keeping your original resume text unchanged to protect formatting.
            </p>
            <div className="mt-8 grid gap-4">
              <div className="border-l-4 border-emerald-600 bg-emerald-50 p-5" data-testid="resume-tailor-format-card">
                <ShieldCheck className="h-6 w-6 text-emerald-600" />
                <p className="mt-3 text-sm font-bold text-[#0A2540]" data-testid="resume-tailor-format-text">For DOCX/PDF resumes with borders, tables, or columns: keep your original file and copy the suggested points into it. Extracted text cannot recreate visual design.</p>
              </div>
              <div className="border-l-4 border-[#0066FF] bg-[#F8FAFC] p-5" data-testid="resume-tailor-privacy-card">
                <ShieldCheck className="h-6 w-6 text-[#0066FF]" />
                <p className="mt-3 text-sm font-bold text-[#0A2540]" data-testid="resume-tailor-privacy-text">Privacy-friendly: resume and job text are processed for the request and not stored.</p>
              </div>
              <div className="border-l-4 border-[#0066FF] bg-[#F8FAFC] p-5" data-testid="resume-tailor-download-card">
                <Download className="h-6 w-6 text-[#0066FF]" />
                <p className="mt-3 text-sm font-bold text-[#0A2540]" data-testid="resume-tailor-download-text">Download as Word-compatible file or use print to save as PDF.</p>
              </div>
            </div>
            <div className="mt-6">
              <ShareByEmail
                title="Share Resume Tailor"
                description="Know someone applying for jobs? Enter their email and send them the Resume Tailor link."
                path="/resume-tailor"
                subject="Free resume tailoring tool from TalentBridge Consulting"
                body="Hi, I thought this free Resume Tailor tool may help you improve your resume for a job posting. Here is the link:"
                testId="resume-tailor"
              />
            </div>
          </div>

          <form onSubmit={submit} className="grid gap-5 border border-[#E2E8F0] bg-[#FBFBFC] p-6 md:p-8" data-testid="resume-tailor-form">
            {error && <div className="border border-red-200 bg-red-50 p-3 text-sm font-bold text-red-700" data-testid="resume-tailor-error-message">{error}</div>}
            <label className="grid gap-2" data-testid="tailor-target-role-field">
              <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#475569]" data-testid="tailor-target-role-label">Target role optional</span>
              <input value={targetRole} onChange={(event) => setTargetRole(event.target.value)} placeholder="Example: Project Coordinator" className="min-h-11 border border-[#CBD5E1] bg-white px-3 text-sm text-[#0B1120] focus:outline-none focus:ring-2 focus:ring-[#0066FF]" data-testid="tailor-target-role-input" />
            </label>
            <label className="grid gap-2" data-testid="tailor-job-category-field">
              <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#475569]" data-testid="tailor-job-category-label">Job category</span>
              <select value={jobCategory} onChange={(event) => setJobCategory(event.target.value)} className="min-h-11 border border-[#CBD5E1] bg-white px-3 text-sm text-[#0B1120] focus:outline-none focus:ring-2 focus:ring-[#0066FF]" data-testid="tailor-job-category-input">
                {jobCategories.map((category) => <option key={category} value={category}>{category}</option>)}
              </select>
            </label>
            <label className="grid gap-2" data-testid="tailor-job-field">
              <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#475569]" data-testid="tailor-job-label">Paste job responsibilities</span>
              <textarea value={jobResponsibilities} onChange={(event) => setJobResponsibilities(event.target.value)} required rows={10} placeholder="Paste the job responsibilities or job description here..." className="border border-[#CBD5E1] bg-white px-3 py-3 text-sm leading-6 text-[#0B1120] focus:outline-none focus:ring-2 focus:ring-[#0066FF]" data-testid="tailor-job-input" />
            </label>
            <label className="grid gap-2" data-testid="tailor-resume-field">
              <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#475569]" data-testid="tailor-resume-label">Paste resume or upload file</span>
              <ResumeUploadBox
                testId="resume-tailor"
                loading={uploading}
                setLoading={setUploading}
                setError={setError}
                onParsed={(text, meta) => {
                  setResumeText(text);
                  setUploadInfo(meta);
                }}
              />
              {uploadInfo && <p className="text-xs font-bold text-emerald-700" data-testid="tailor-upload-success-text">Loaded {uploadInfo.filename} · approx. {uploadInfo.word_count} words</p>}
              <textarea value={resumeText} onChange={(event) => setResumeText(event.target.value)} required rows={14} placeholder="Paste your current resume here..." className="border border-[#CBD5E1] bg-white px-3 py-3 text-sm leading-6 text-[#0B1120] focus:outline-none focus:ring-2 focus:ring-[#0066FF]" data-testid="tailor-resume-input" />
            </label>
            <label className="flex items-start gap-3 border border-[#CBD5E1] bg-white p-4" data-testid="tailor-storage-consent-field">
              <input type="checkbox" checked={consentToStore} onChange={(event) => setConsentToStore(event.target.checked)} className="mt-1 h-4 w-4" data-testid="tailor-storage-consent-input" />
              <span className="text-sm font-semibold leading-6 text-[#0A2540]" data-testid="tailor-storage-consent-text">
                Optional: I agree TalentBridge can save this tailored resume for job matching. If checked, your tailored resume may be reviewed by TalentBridge for job matching.
              </span>
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <button type="button" onClick={() => { setResumeText(sampleResume); setJobResponsibilities(sampleJob); setTargetRole("Project Coordinator"); setJobCategory("Project & Program Management"); }} className="border border-[#0A2540] px-5 py-3 text-sm font-bold text-[#0A2540] hover:bg-[#0A2540] hover:text-white" data-testid="tailor-load-sample-button">Load sample</button>
              <button type="submit" disabled={loading || uploading} className="inline-flex items-center justify-center gap-2 bg-[#0066FF] px-5 py-3 text-sm font-bold text-white hover:bg-[#0052CC] disabled:opacity-60" data-testid="tailor-submit-button">
                {loading ? "Tailoring..." : "Tailor my resume"} <Sparkles className="h-4 w-4" />
              </button>
            </div>
          </form>
        </div>
      </section>

      {result && (
        <section className="bg-[#F8FAFC] px-5 py-16 md:px-8 lg:py-24" data-testid="tailor-results-section">
          <div className="mx-auto grid max-w-7xl gap-8">
            <div className="grid gap-6 lg:grid-cols-[0.35fr_1fr]" data-testid="tailor-summary-grid">
              <div className="border border-[#E2E8F0] bg-[#0A2540] p-8 text-white" data-testid="tailor-score-card">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-200" data-testid="tailor-score-label">Job match</p>
                <p className="mt-4 font-heading text-7xl font-black" data-testid="tailor-match-score-value">{result.match_score}</p>
                <p className="mt-5 text-sm leading-7 text-slate-200" data-testid="tailor-summary-text">{result.summary}</p>
              </div>
              <div className="grid gap-4 md:grid-cols-2" data-testid="tailor-insights-grid">
                <div className="border border-[#E2E8F0] bg-white p-5" data-testid="tailor-added-card">
                  <h3 className="font-heading text-xl font-black text-[#0A2540]" data-testid="tailor-added-title">Suggested points to copy</h3>
                  <ul className="mt-4 grid gap-2 text-sm leading-6 text-[#475569]">
                    {result.missing_responsibilities_added.map((item, index) => <li key={`${item}-${index}`} data-testid={`tailor-added-item-${index}`}>• {item}</li>)}
                  </ul>
                </div>
                <div className="border border-[#E2E8F0] bg-white p-5" data-testid="tailor-keywords-card">
                  <h3 className="font-heading text-xl font-black text-[#0A2540]" data-testid="tailor-keywords-title">ATS keywords</h3>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {result.ats_keywords.map((keyword, index) => <span key={`${keyword}-${index}`} className="bg-blue-50 px-3 py-1 text-xs font-bold text-[#0066FF]" data-testid={`tailor-keyword-${index}`}>{keyword}</span>)}
                  </div>
                </div>
                <div className="border border-[#E2E8F0] bg-white p-5" data-testid="tailor-sections-card">
                  <h3 className="font-heading text-xl font-black text-[#0A2540]" data-testid="tailor-sections-title">Formatting status</h3>
                  <p className="mt-4 text-sm leading-7 text-[#475569]" data-testid="tailor-sections-text">{result.sections_updated.join(", ")}</p>
                </div>
                <div className="border border-[#E2E8F0] bg-white p-5" data-testid="tailor-notes-card">
                  <h3 className="font-heading text-xl font-black text-[#0A2540]" data-testid="tailor-notes-title">Editing notes</h3>
                  <ul className="mt-4 grid gap-2 text-sm leading-6 text-[#475569]">
                    {result.editing_notes.map((note, index) => <li key={`${note}-${index}`} data-testid={`tailor-note-${index}`}>• {note}</li>)}
                  </ul>
                </div>
              </div>
            </div>

            <div className="border border-[#E2E8F0] bg-white p-6" data-testid="tailor-editor-card">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="font-heading text-2xl font-black text-[#0A2540]" data-testid="tailor-editor-title">Original resume kept unchanged</h2>
                  <p className="mt-2 text-sm text-[#64748B]" data-testid="tailor-word-count-text">Approx. {wordCount} words. To keep exact formatting, copy suggested points into your original Word/PDF file.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {uploadInfo?.originalFile && <button type="button" onClick={downloadOriginalFile} className="inline-flex items-center gap-2 bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700" data-testid="tailor-download-original-file-button"><Download className="h-4 w-4" /> Download original DOCX/PDF</button>}
                  <button type="button" onClick={printResume} className="inline-flex items-center gap-2 border border-[#CBD5E1] px-4 py-2 text-sm font-bold text-[#0A2540] hover:border-[#0066FF]" data-testid="tailor-print-button"><Printer className="h-4 w-4" /> Print original text</button>
                  <button type="button" onClick={downloadWord} className="inline-flex items-center gap-2 bg-[#0066FF] px-4 py-2 text-sm font-bold text-white hover:bg-[#0052CC]" data-testid="tailor-download-word-button"><FileText className="h-4 w-4" /> Download original Word</button>
                  <button type="button" onClick={downloadText} className="inline-flex items-center gap-2 border border-[#CBD5E1] px-4 py-2 text-sm font-bold text-[#0A2540] hover:border-[#0066FF]" data-testid="tailor-download-text-button"><Download className="h-4 w-4" /> Download original TXT</button>
                </div>
              </div>
              <textarea value={editableResume} onChange={(event) => setEditableResume(event.target.value)} rows={28} className="mt-6 w-full border border-[#CBD5E1] bg-[#FBFBFC] px-4 py-4 font-mono text-sm leading-7 text-[#0B1120] focus:outline-none focus:ring-2 focus:ring-[#0066FF]" data-testid="tailor-editable-resume-input" />
              <p className="mt-4 border border-amber-200 bg-amber-50 p-3 text-xs font-bold leading-5 text-amber-800" data-testid="tailor-format-warning-text">This text preview cannot preserve Word/PDF borders, tables, columns, fonts, or design elements. Use the green original-file download or your original DOCX/PDF, then manually paste the suggested points.</p>
              <p className="mt-4 text-xs font-semibold text-[#64748B]" data-testid="tailor-privacy-note-text">{result.privacy_note}</p>
              {result.saved_resume_id && <p className="mt-2 text-xs font-bold text-emerald-700" data-testid="tailor-saved-confirmation-text">Saved for TalentBridge job matching review.</p>}
            </div>
          </div>
        </section>
      )}
    </PageShell>
  );
}